import { useState, useCallback, memo } from 'react';
import { RotateCcw } from 'lucide-react';
import ResultatsElevePanel from './ResultatsElevePanel';
import { getInitials, getScoreColor, CARD_BG } from './ResultatsExercice';

/**
 * Vue "Par élève" — group chips + cartes élèves + panel détail.
 * Affiche les élèves groupés par niveau de réussite.
 */
const ResultatsExerciceByEleve = memo(function ResultatsExerciceByEleve({
  statsParEleve, elevesNonRepondus, questions, isEnLigne, isEquipe,
  handleResetEleve, isResetting, setPreviewQuestion
}) {
  const [filterGroup, setFilterGroup] = useState(null);
  const [showAbsent, setShowAbsent] = useState(false);
  const [selectedEleve, setSelectedEleve] = useState(null);
  const [focusQuestion, setFocusQuestion] = useState(null);

  // Groupes de niveau
  const groups = {
    mastered: { eleves: statsParEleve.filter(e => e.score === e.total), label: 'Maîtrisé', gradient: 'from-emerald-500 to-teal-500', light: 'bg-emerald-50 border-emerald-200', dot: 'bg-emerald-400' },
    partial: { eleves: statsParEleve.filter(e => e.score > 0 && e.score < e.total), label: 'En cours', gradient: 'from-amber-500 to-orange-500', light: 'bg-amber-50 border-amber-200', dot: 'bg-amber-400' },
    struggle: { eleves: statsParEleve.filter(e => e.score === 0), label: 'À renforcer', gradient: 'from-red-500 to-rose-500', light: 'bg-red-50 border-red-200', dot: 'bg-red-400' },
    absent: { eleves: elevesNonRepondus || [], label: 'En attente', gradient: 'from-gray-400 to-slate-400', light: 'bg-gray-50 border-gray-200', dot: 'bg-gray-400' },
  };

  const totalEleves = statsParEleve.length + (elevesNonRepondus?.length || 0);

  // Élèves filtrés
  let filtered = statsParEleve;
  if (filterGroup === 'mastered') filtered = groups.mastered.eleves;
  else if (filterGroup === 'partial') filtered = groups.partial.eleves;
  else if (filterGroup === 'struggle') filtered = groups.struggle.eleves;
  else if (filterGroup === 'absent') filtered = [];

  // Ouvrir le panel élève
  const handleDotClick = useCallback((eleve, questionOrdre) => {
    setSelectedEleve(eleve);
    setFocusQuestion(questionOrdre);
  }, []);

  // Clic sur la carte (hors dot) → ouvre le panel à Q1
  const handleCardClick = useCallback((eleve) => {
    setSelectedEleve(eleve);
    setFocusQuestion(questions?.[0]?.question_ordre || null);
  }, [questions]);

  return (
    <div className="space-y-4">
      {/* Group chips */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {Object.entries(groups).filter(([, g]) => g.eleves.length > 0).map(([key, g]) => {
          const isActive = filterGroup === key;
          const pct = totalEleves > 0 ? Math.round((g.eleves.length / totalEleves) * 100) : 0;
          return (
            <button
              key={key}
              onClick={() => setFilterGroup(prev => prev === key ? null : key)}
              className={`rounded-2xl border-2 p-3.5 text-left transition-all duration-300 ${
                isActive
                  ? 'border-blue-500 shadow-lg shadow-blue-500/15 scale-[1.02]'
                  : `border-transparent ${g.light} hover:shadow-md hover:scale-[1.01]`
              }`}
              style={{ background: CARD_BG }}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className={`w-2.5 h-2.5 rounded-full ${g.dot}`} />
                  <span className="text-[11px] font-semibold text-gray-600 uppercase tracking-wider">{g.label}</span>
                </div>
                <span className="text-[10px] font-medium text-gray-400">{pct}%</span>
              </div>
              <div className="flex items-end justify-between">
                <span className="text-2xl font-black text-gray-800">{g.eleves.length}</span>
                <div className="w-12 h-1.5 rounded-full bg-gray-200 overflow-hidden mb-1.5">
                  <div className={`h-full rounded-full bg-gradient-to-r ${g.gradient} transition-all duration-500`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Élèves qui ont répondu */}
      {filterGroup !== 'absent' && (
        <>
          <p className="text-[11px] text-gray-400 italic">Cliquer sur une question pour voir la réponse de l'élève</p>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Ont répondu ({statsParEleve.length}/{totalEleves})
          </p>
        </>
      )}

      {/* Cartes élèves */}
      <div className="flex flex-wrap gap-2 items-start">
        {filtered.map((eleve) => {
          const scoreColor = getScoreColor(eleve.score, eleve.total);
          return (
            <div
              key={eleve.nom}
              onClick={() => handleCardClick(eleve)}
              className="relative flex flex-col gap-2 px-3 py-2.5 rounded-xl overflow-hidden cursor-pointer
               border border-blue-100/60 shadow-md shadow-blue-900/10
               hover:shadow-lg hover:shadow-blue-900/15 hover:border-blue-300
               hover:-translate-y-0.5 transition-all duration-200 group
               flex-1 min-w-[280px] sm:min-w-[320px]"
              style={{ background: CARD_BG }}
            >
              {/* Ligne 1 — Avatar + Nom + Score */}
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-[11px]
                font-bold text-white flex-shrink-0 border-2 border-sky-600"
                style={{ background: 'linear-gradient(135deg, #3B82F6, #06B6D4)',
                boxShadow: '0 4px 12px rgba(59,130,246,0.25)' }}>
                  {getInitials(eleve.nom)}
                </div>
                <span className="flex-1 text-sm font-medium text-gray-700 group-hover:text-gray-900 transition-colors">
                  {eleve.nom}
                </span>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <span className={`text-sm font-extrabold ${scoreColor}`}>
                    {eleve.score}/{eleve.total}
                  </span>
                  {isEnLigne && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`Autoriser ${eleve.nom} à repasser le quiz ?`)) handleResetEleve(eleve);
                      }}
                      disabled={isResetting === eleve.id_eleve}
                      title="Autoriser à repasser"
                      className={`w-7 h-7 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-500 hover:bg-amber-100 hover:border-amber-300 hover:text-amber-600 transition-all hover:scale-110 opacity-0 group-hover:opacity-100 ${isResetting === eleve.id_eleve ? 'opacity-100 animate-spin' : ''}`}
                    >
                      <RotateCcw size={13} />
                    </button>
                  )}
                </div>
              </div>
              {/* Ligne 2 — Dots Q1/Q2/Q3... */}
              <div className="flex flex-wrap gap-1.5 pl-12">
                {(questions || []).map((q) => {
                  const rep = eleve.reponses.find(r => r.question === q.question_ordre);
                  if (!rep) return (
                    <div key={q.question_ordre} className="flex flex-col items-center gap-0.5">
                      <span className="text-[8px] font-bold text-gray-300">Q{q.question_ordre}</span>
                      <div className="w-6 h-6 rounded-md bg-gray-100 border border-gray-200 flex items-center justify-center text-[10px] text-gray-300">—</div>
                    </div>
                  );
                  return (
                    <div key={q.question_ordre} className="flex flex-col items-center gap-0.5">
                      <span className="text-[8px] font-bold text-gray-400">Q{q.question_ordre}</span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleDotClick(eleve, q.question_ordre); }}
                        className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold border transition-transform hover:scale-110 ${
                          rep.correcte ? 'bg-emerald-50 border-emerald-300 text-emerald-600' : 'bg-red-50 border-red-300 text-red-500'
                        }`}
                      >
                        {rep.correcte ? '✓' : '✗'}
                      </button>
                    </div>
                  );
                })}
              </div>
              {/* Hover hint */}
              <span className="absolute bottom-1 right-3 text-[10px] text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity">
                →
              </span>
            </div>
          );
        })}
        {filtered.length === 0 && filterGroup !== 'absent' && (
          <p className="text-sm text-gray-400 italic py-4 w-full text-center">Aucun élève dans cette catégorie</p>
        )}
      </div>

      {/* En attente — collapsed par défaut */}
      {(elevesNonRepondus?.length || 0) > 0 && filterGroup !== 'absent' && (
        <div className="mt-4">
          <button
            onClick={() => setShowAbsent(prev => !prev)}
            className="flex items-center gap-2 text-xs font-semibold text-gray-400 uppercase tracking-wider hover:text-gray-600 transition-colors"
          >
            <span className={`transition-transform duration-200 ${showAbsent ? 'rotate-90' : ''}`}>▸</span>
            En attente ({elevesNonRepondus.length})
          </button>
          {showAbsent && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {elevesNonRepondus.map(e => (
                <span key={e.nom} className="px-2.5 py-1 rounded-lg bg-gray-100 border border-gray-200 text-xs text-gray-500 font-medium">
                  {e.nom}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Si filtre "absent" actif → afficher la liste en plein */}
      {filterGroup === 'absent' && (
        <div className="flex flex-wrap gap-1.5">
          {(elevesNonRepondus || []).map(e => (
            <span key={e.nom} className="px-2.5 py-1 rounded-lg bg-gray-100 border border-gray-200 text-xs text-gray-500 font-medium">
              {e.nom}
            </span>
          ))}
        </div>
      )}

      {/* Panel élève — portal avec toutes les questions */}
      <ResultatsElevePanel
        selectedEleve={selectedEleve}
        focusQuestion={focusQuestion}
        questions={questions}
        isEnLigne={isEnLigne}
        onClose={() => setSelectedEleve(null)}
        onFocusConsumed={() => setFocusQuestion(null)}
        setPreviewQuestion={setPreviewQuestion}
      />
    </div>
  );
});

export default ResultatsExerciceByEleve;
